'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

import { useAuth } from '$lib/app/auth-context';
import { AppShell } from '$lib/app/app-shell';
import { canAccessRoute, isPublicPath, matchRoute } from '$lib/app/routes';
import {
	AccountPasswordPage,
	AccountContractsPage,
	AccountPayslipsPage,
	AccountProfilePage,
	AdminConsolePage,
	AgentsPage,
	ApprovalDetailPage,
	ApprovalLinesPage,
	ApprovalsPage,
	AttendancePage,
	BillingCheckoutPage,
	BillingPaymentDetailPage,
	BillingPage,
	CertificateVerifyPage,
	CertificatesPage,
	CompanySettingsPage,
	CompanySetupPage,
	CustomerSuccessPage,
	DashboardPage,
	DigitalContractsPage,
	EmployeeDetailPage,
	EmployeesPage,
	EmployeeNumberPolicyPage,
	EngagementPage,
	EmployeeLifecyclePage,
	ExpensesPage,
	FieldOperationsPage,
	ForgotPasswordPage,
	GroupInsurancePage,
	HomePage,
	HrInsightsPage,
	HolidaysPage,
	InviteSignupPage,
	LandingPage,
	LearningOperationsPage,
	LeaveAdminPage,
	LeavePage,
	LeavePolicyPage,
	LoginPage,
	MeetingsPage,
	NewsFeedPage,
	NotificationsPage,
	OneOnOnesPage,
	OpenApiClientsPage,
	OrganizationPage,
	OrganizationInsightsPage,
	PartnerAdvisoryPage,
	GoalsPage,
	EmployeePayslipsPage,
	PayrollImportDetailPage,
	PayrollImportsPage,
	PayrollPage,
	PayrollPayslipsPage,
	PayslipDetailPage,
	RecruitingPage,
	ReportsPage,
	ResourcePage,
	ReviewsPage,
	ResetPasswordPage,
	SecurityCompliancePage,
	SignupPage,
	WorkflowsPage,
	WorkPoliciesPage,
	YearEndSettlementPage
} from '$lib/app/pages';
import { Card, CardContent, Spinner } from '$lib/ui';

function PublicSurface({ path }: { path: string }) {
	const route = matchRoute(path);
	switch (route.definition.surface) {
		case 'landing':
			return <LandingPage />;
		case 'login':
			return <LoginPage />;
		case 'signup':
			return <SignupPage />;
		case 'invite-signup':
			return <InviteSignupPage />;
		case 'forgot-password':
			return <ForgotPasswordPage />;
		case 'reset-password':
			return <ResetPasswordPage />;
		case 'certificate-verify':
			return <CertificateVerifyPage />;
		default:
			return <ResourcePage route={route} />;
	}
}

export function RoutePage({ path }: { path: string }) {
	if (isPublicPath(path)) return <PublicSurface path={path} />;
	return <ProtectedRoutePage path={path} />;
}

function ProtectedRoutePage({ path }: { path: string }) {
	const auth = useAuth();
	const router = useRouter();
	const route = matchRoute(path);

	useEffect(() => {
		if (auth.loading || auth.isLoggedIn) return;
		if (typeof window !== 'undefined' && sessionStorage.getItem('antflow:logoutRedirect') === '1') {
			sessionStorage.removeItem('antflow:logoutRedirect');
			router.replace('/login');
			return;
		}
		router.replace(`/login?next=${encodeURIComponent(path)}`);
	}, [auth.loading, auth.isLoggedIn, path, router]);

	useEffect(() => {
		if (auth.loading || !auth.isLoggedIn) return;
		if (
			auth.isSuperAdmin &&
			!auth.isImpersonating &&
			!path.startsWith('/admin') &&
			!path.startsWith('/account')
		) {
			router.replace('/admin');
		}
	}, [auth.loading, auth.isImpersonating, auth.isLoggedIn, auth.isSuperAdmin, path, router]);

	if (auth.loading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<Spinner size="lg" />
			</div>
		);
	}

	if (!auth.isLoggedIn) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background p-4">
				<Card className="w-full max-w-md">
					<CardContent className="p-6 text-center text-sm text-muted-foreground">
						로그인 화면으로 이동 중입니다.
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!canAccessRoute(route.definition, auth.user?.role ?? null)) {
		return (
			<AppShell>
				<Card>
					<CardContent className="p-6 text-sm text-muted-foreground">
						접근 권한이 없는 화면입니다.
					</CardContent>
				</Card>
			</AppShell>
		);
	}

	if (route.definition.surface === 'company-setup') {
		return (
			<AppShell>
				<CompanySetupPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/dashboard') {
		return (
			<AppShell>
				<DashboardPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/insights/hr') {
		return (
			<AppShell>
				<HrInsightsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/agents') {
		return (
			<AppShell>
				<AgentsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/home') {
		return (
			<AppShell>
				<HomePage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/employees') {
		return (
			<AppShell>
				<EmployeesPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/employees/:id') {
		return (
			<AppShell>
				<EmployeeDetailPage route={route} />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/attendance') {
		return (
			<AppShell>
				<AttendancePage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/field-operations') {
		return (
			<AppShell>
				<FieldOperationsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/shifts') {
		return (
			<AppShell>
				<FieldOperationsPage mode="shift" />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/partners/advisory') {
		return (
			<AppShell>
				<PartnerAdvisoryPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/customer-success') {
		return (
			<AppShell>
				<CustomerSuccessPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/engagement') {
		return (
			<AppShell>
				<EngagementPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/news-feed') {
		return (
			<AppShell>
				<NewsFeedPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/employee-lifecycle') {
		return (
			<AppShell>
				<EmployeeLifecyclePage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/learning') {
		return (
			<AppShell>
				<LearningOperationsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/organization') {
		return (
			<AppShell>
				<OrganizationPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/organization/insights') {
		return (
			<AppShell>
				<OrganizationInsightsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/performance/goals') {
		return (
			<AppShell>
				<GoalsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/performance/reviews') {
		return (
			<AppShell>
				<ReviewsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/performance/one-on-ones') {
		return (
			<AppShell>
				<OneOnOnesPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/meetings') {
		return (
			<AppShell>
				<MeetingsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/leave') {
		return (
			<AppShell>
				<LeavePage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/leave/admin') {
		return (
			<AppShell>
				<LeaveAdminPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/employees/number-policy') {
		return (
			<AppShell>
				<EmployeeNumberPolicyPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/leave/policy') {
		return (
			<AppShell>
				<LeavePolicyPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/approvals') {
		return (
			<AppShell>
				<ApprovalsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/workflows') {
		return (
			<AppShell>
				<WorkflowsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/recruiting') {
		return (
			<AppShell>
				<RecruitingPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/digital-contracts') {
		return (
			<AppShell>
				<DigitalContractsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/approvals/:id') {
		return (
			<AppShell>
				<ApprovalDetailPage route={route} />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/approval-lines') {
		return (
			<AppShell>
				<ApprovalLinesPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/payroll') {
		return (
			<AppShell>
				<PayrollPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/payroll/imports') {
		return (
			<AppShell>
				<PayrollImportsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/payroll/imports/:id') {
		return (
			<AppShell>
				<PayrollImportDetailPage route={route} />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/year-end-settlement') {
		return (
			<AppShell>
				<YearEndSettlementPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/group-insurance') {
		return (
			<AppShell>
				<GroupInsurancePage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/payroll/payslips') {
		return (
			<AppShell>
				<PayrollPayslipsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/payroll/payslips/:employeeId') {
		return (
			<AppShell>
				<EmployeePayslipsPage route={route} />
			</AppShell>
		);
	}

	if (
		route.definition.pattern === '/payroll/payslips/:employeeId/:payslipId' ||
		route.definition.pattern === '/account/payslips/:employeeId/:payslipId' ||
		route.definition.pattern === '/employees/:employeeId/payslips/:payslipId'
	) {
		return (
			<AppShell>
				<PayslipDetailPage route={route} />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/reports') {
		return (
			<AppShell>
				<ReportsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/security') {
		return (
			<AppShell>
				<SecurityCompliancePage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/open-api') {
		return (
			<AppShell>
				<OpenApiClientsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/billing') {
		return (
			<AppShell>
				<BillingPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/billing/checkout') {
		return (
			<AppShell>
				<BillingCheckoutPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/billing/payments/:id') {
		return (
			<AppShell>
				<BillingPaymentDetailPage route={route} />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/expenses') {
		return (
			<AppShell>
				<ExpensesPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/certificates') {
		return (
			<AppShell>
				<CertificatesPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/holidays') {
		return (
			<AppShell>
				<HolidaysPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/work-policies') {
		return (
			<AppShell>
				<WorkPoliciesPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/company/settings') {
		return (
			<AppShell>
				<CompanySettingsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/account/profile') {
		return (
			<AppShell>
				<AccountProfilePage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/account/payslips') {
		return (
			<AppShell>
				<AccountPayslipsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/account/contracts') {
		return (
			<AppShell>
				<AccountContractsPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/account/password') {
		return (
			<AppShell>
				<AccountPasswordPage />
			</AppShell>
		);
	}

	if (route.definition.pattern === '/notifications') {
		return (
			<AppShell>
				<NotificationsPage />
			</AppShell>
		);
	}

	if (route.definition.domain === 'admin') {
		return (
			<AppShell>
				<AdminConsolePage route={route} />
			</AppShell>
		);
	}

	return (
		<AppShell>
			<ResourcePage route={route} />
		</AppShell>
	);
}
